/**
 * Input-to-photon latency: the wire contract.
 *
 * This is the latency a player actually feels — the span from touching the screen to the
 * first frame that could show the result. It covers the whole loop: DOM event, data
 * channel, host receive, IPC, Playjector, driver injection, game render, capture, encode,
 * network, jitter buffer, decode, and display.
 *
 * WHY THIS IS MEASURABLE WITHOUT CLOCK SYNC
 * Both ends of the measurement sit on the GUEST clock: the input event fires there, and
 * the frame is presented there. The host never contributes a timestamp, only a correlation
 * key. That is the whole reason this metric is trustworthy where glass-to-glass is not —
 * glass-to-glass spans two machines and needs the probe handshake's clock offset before it
 * can report anything, and any error in that offset lands directly in the result.
 *
 * HOW THE CORRELATION WORKS
 *  1. Guest stamps a sampled input with `inputSeq` and remembers the event time locally.
 *  2. Host forwards that input for injection and remembers the seq as pending.
 *  3. The host's encoded-frame transform tags the next frame it sees and sends the guest
 *     an `InputPhotonTag`: "RTP timestamp R is the first frame encoded after input N".
 *  4. The guest's rVFC fires when frame R is presented; the delta against the remembered
 *     event time is the measurement.
 *
 * WHAT THIS DOES AND DOES NOT MEASURE
 * It measures to the first frame that COULD contain the response, not the frame where the
 * game visibly reacts. A title that takes three frames to animate a button press adds that
 * on top, and that part is not attributable to the streaming stack. Treat the number as
 * the pipeline's floor, not as the player's perceived reaction time.
 *
 * Quantization: "the next frame encoded after injection" is accurate to at most one frame
 * interval, so readings carry up to ~17ms (60fps) or ~33ms (30fps) of granularity error.
 */

/** Datachannel message type for the host's frame tag. */
export const INPUT_PHOTON_MESSAGE_TYPE = 'inputPhoton';

/**
 * Host → guest: the first frame encoded after `inputSeq` was forwarded for injection
 * carries `rtpTimestamp`.
 */
export type InputPhotonTag = {
  type: typeof INPUT_PHOTON_MESSAGE_TYPE;
  role: 'tag';
  inputSeq: number;
  rtpTimestamp: number;
};

/** A single completed input-to-photon measurement. */
export type InputPhotonSample = {
  inputSeq: number;
  rtpTimestamp: number;
  latencyMs: number;
};

/**
 * Where the correlation chain has and has not been reached this session. Lifetime counters,
 * so a summary that reads `sampleCount: 0` can also say which hop went dark: inputs sampled
 * but no tags back names the host or the channel; tags back but no frames seen names rVFC;
 * frames seen but nothing matched names the RTP-timestamp correlation.
 */
export type InputPhotonChainCounts = {
  /** Inputs given a sequence number and sent tagged. */
  sampled: number;
  /** Host tags received for an input still pending. */
  tagsAccepted: number;
  /** Host tags received for an input no longer pending (expired or already matched). */
  tagsIgnored: number;
  /** Presented frames that carried an RTP timestamp and were offered for correlation. */
  framesSeen: number;
  /** Correlations completed but discarded as implausible. */
  implausible: number;
};

/** Rolling view for the debug log and stream-health snapshot. */
export type InputPhotonSummary = {
  /** Most recent measurement, or null before the first correlation lands. */
  lastMs: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
  sampleCount: number;
  chain: InputPhotonChainCounts;
};

/**
 * Minimum gap between sampled inputs, ms.
 *
 * Input can fire every frame (pointermove, gamepad polling). Measuring all of it would put
 * meaningful traffic on the datachannel to describe a quantity that moves slowly. Two
 * readings a second is plenty to watch a trend, and sampling discrete presses rather than
 * continuous motion keeps the samples aligned with what a player notices as lag.
 */
export const INPUT_PHOTON_SAMPLE_INTERVAL_MS = 500;

/** Rolling window for the percentile summary. At 2 samples/sec this is ~30s of history. */
export const INPUT_PHOTON_WINDOW_SAMPLES = 60;

/**
 * How long an un-correlated pending input stays eligible, ms.
 *
 * A sampled input whose frame tag never arrives (tag lost, frame dropped, encoder stalled)
 * must expire. Without this the pending map is a slow leak, and a tag arriving long after
 * the fact would report a latency that is really just the age of a forgotten record.
 */
export const INPUT_PHOTON_MAX_AGE_MS = 5_000;

/**
 * Upper bound on a believable reading, ms.
 *
 * Anything past this is a correlation accident (RTP timestamp reuse after an SSRC reset,
 * a tab resumed from background) rather than a real measurement, and publishing it would
 * poison the percentiles far longer than it takes to notice.
 */
export const INPUT_PHOTON_MAX_PLAUSIBLE_MS = 3_000;

/**
 * Metadata key carrying the sampled input's sequence number.
 *
 * It rides the message's existing timing metadata rather than a new envelope field. The
 * host already appends a timing entry to every input before forwarding, and those messages
 * continue on to Playjector's C++ JSON parser — so reusing a channel that demonstrably
 * tolerates growth avoids putting a schema change in front of the native layer for the
 * sake of a debug metric.
 */
export const INPUT_PHOTON_SEQ_METADATA_KEY = 'input_photon_seq';

/** Structural view of the envelope, so this stays free of a Messaging dependency. */
type TimingCarrier = {
  body?: { timings?: { metadata?: Record<string, string> | undefined }[] | undefined } | undefined;
};

/** Read a sampled input's sequence number, or null when the input was not sampled. */
export function readInputPhotonSeq(message: TimingCarrier): number | null {
  const timings = message?.body?.timings;
  if (!Array.isArray(timings)) return null;
  for (const entry of timings) {
    const raw = entry?.metadata?.[INPUT_PHOTON_SEQ_METADATA_KEY];
    if (raw === undefined) continue;
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function isInputPhotonTag(value: unknown): value is InputPhotonTag {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<InputPhotonTag>;
  return (
    candidate.type === INPUT_PHOTON_MESSAGE_TYPE &&
    candidate.role === 'tag' &&
    typeof candidate.inputSeq === 'number' &&
    Number.isFinite(candidate.inputSeq) &&
    typeof candidate.rtpTimestamp === 'number' &&
    Number.isFinite(candidate.rtpTimestamp)
  );
}

/**
 * Whether a raw latency reading is publishable.
 *
 * Negative readings mean the clocks moved under us (a suspended tab, a `performance.now`
 * origin change) and are never real.
 */
export function isPlausibleInputPhotonLatency(latencyMs: number): boolean {
  return (
    Number.isFinite(latencyMs) && latencyMs >= 0 && latencyMs <= INPUT_PHOTON_MAX_PLAUSIBLE_MS
  );
}
